import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HospitalAdminKpis } from '@/types/kpis';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users,
  Calendar,
  TrendingUp,
  CheckCircle,
  XCircle,
  RefreshCw,
  IndianRupee,
  Activity,
  UserCheck,
  FlaskConical,
  FlaskConicalOff,
  Building2
} from 'lucide-react';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { format } from 'date-fns';

type IntervalOption = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'custom_month' | 'custom_year';

export default function HospitalDashboard() {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [selectedInterval, setSelectedInterval] = useState<IntervalOption>('today');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  if (user?.role === 'SALES_MANAGER') {
    return <Navigate to="/hospital-admin/leads" replace />;
  }

  const handleTimeIntervalChange = React.useCallback((start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleIntervalChange = React.useCallback((interval: IntervalOption) => {
    setSelectedInterval(interval);
  }, []);

  const handleMonthChange = React.useCallback((month: number) => {
    setSelectedMonth(month);
  }, []);

  const handleYearChange = React.useCallback((year: number) => {
    setSelectedYear(year);
  }, []);

  const { data: kpis, isLoading, error } = useQuery<HospitalAdminKpis>({
    queryKey: ['hospital-admin-kpis', startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async () => {

    if (startDate && endDate) {
      return await hospitalAdminApi.getKpisByDate(startDate.toISOString(), endDate.toISOString());
      }
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0));
      const endOfDay = new Date(today.setHours(23, 59, 59));
    return await hospitalAdminApi.getKpisByDate(startOfDay.toISOString(), endOfDay.toISOString());
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-artis-gold"></div>
          <p className="text-artis-slate">Loading hospital dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load hospital data</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-artis-gold px-4 py-2 text-white hover:bg-artis-gold/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Appointments',
      value: kpis?.totalAppointments || 0,
      icon: Calendar,
      color: 'from-blue-500 to-indigo-600',
      description: 'All appointments scheduled'
    },
    {
      title: 'Total Patients',
      value: kpis?.totalPatients || 0,
      icon: Users,
      color: 'from-emerald-500 to-teal-600',
      description: 'Registered patients'
    },
    {
      title: 'Active Patients',
      value: kpis?.activePatients || 0,
      icon: UserCheck,
      color: 'from-teal-500 to-cyan-600',
      description: 'Patients with appointments'
    },
    {
      title: 'Total Revenue',
      value: `₹${(kpis?.totalRevenue || 0).toLocaleString()}`,
      icon: IndianRupee,
      color: 'from-artis-gold to-amber-600',
      description: 'Total revenue generated'
    },
    {
      title: 'Total Staff',
      value: kpis?.totalStaff || 0,
      icon: Building2,
      color: 'from-violet-500 to-purple-600',
      description: 'Hospital staff members'
    },
    {
      title: 'Lab Tests',
      value: kpis?.totalLabTests || 0,
      icon: FlaskConical,
      color: 'from-orange-500 to-amber-600',
      description: 'Total lab tests conducted'
    },
    {
      title: 'Pending Lab Tests',
      value: kpis?.pendingLabTests || 0,
      icon: FlaskConicalOff,
      color: 'from-yellow-500 to-orange-500',
      description: 'Lab tests awaiting completion'
    },
    {
      title: 'Completed Appointments',
      value: kpis?.totalCompletedAppointments || 0,
      icon: CheckCircle,
      color: 'from-green-600 to-emerald-700',
      description: 'Successfully diagnosed'
    },
    {
      title: 'Follow-ups Scheduled',
      value: kpis?.totalFollowUps || 0,
      icon: RefreshCw,
      color: 'from-indigo-500 to-blue-600',
      description: 'Follow-up appointments'
    },
    {
      title: 'Cancelled Appointments',
      value: kpis?.totalCancelledAppointments || 0,
      icon: XCircle,
      color: 'from-rose-500 to-red-600',
      description: 'Appointments cancelled'
    }
  ];

  // Calculate completion rate
  const completionRate = kpis?.totalAppointments
    ? Math.round((kpis.totalCompletedAppointments / kpis.totalAppointments) * 100)
    : 0;

  return (
    <div className="thms-page-shell">
      <div className="thms-page-header">
        <div>
          <h1 className="thms-page-title">Hospital Dashboard</h1>
          <p className="thms-page-subtitle">Complete overview of hospital operations</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <TimeIntervalFilter
            onTimeIntervalChange={handleTimeIntervalChange}
            selectedInterval={selectedInterval}
            onIntervalChange={handleIntervalChange}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            selectedYear={selectedYear}
            onYearChange={handleYearChange}
          />
          <div className="flex items-center gap-2 text-sm text-artis-slate">
            <Activity className="w-4 h-4" />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Date Range Info */}
      {kpis?.period && (
        <div className="thms-info-banner">
          Showing data from {format(new Date(kpis.period.start), 'PPP')} to {format(new Date(kpis.period.end), 'PPP')}
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {kpiCards.map((kpi, index) => {
          const IconComponent = kpi.icon;
          return (
            <Card key={index} className="thms-stat-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-artis-slate">
                  {kpi.title}
                </CardTitle>
                <div className={`rounded-xl bg-gradient-to-br p-2.5 shadow-sm ${kpi.color}`}>
                  <IconComponent className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums text-artis-brown-dark">{kpi.value}</div>
                <p className="mt-1 text-xs text-artis-slate">{kpi.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="thms-stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-artis-serif text-artis-brown-dark">
              <span className="rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 p-2 shadow-sm">
                <TrendingUp className="h-4 w-4 text-white" />
              </span>
              Hospital Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-artis-slate">Appointment Completion Rate</span>
              <span className="text-lg font-semibold text-emerald-700">{completionRate}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-artis-sand/70">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all duration-300"
                style={{ width: `${completionRate}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-artis-brown-dark">
                  {kpis?.totalAppointments || 0}
                </div>
                <div className="text-xs text-artis-slate">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-700">
                  {kpis?.totalCompletedAppointments || 0}
                </div>
                <div className="text-xs text-artis-slate">Completed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="thms-stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-artis-serif text-artis-brown-dark">
              <span className="rounded-lg bg-gradient-to-br from-artis-gold to-amber-600 p-2 shadow-sm">
                <IndianRupee className="h-4 w-4 text-white" />
              </span>
              Revenue Analytics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-artis-slate">Average Revenue per Appointment</span>
              <span className="text-lg font-semibold text-artis-gold">
                ₹{kpis?.averageRevenuePerAppointment?.toLocaleString() || 0}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-artis-brown-dark">
                  ₹{(kpis?.totalRevenue || 0).toLocaleString()}
                </div>
                <div className="text-xs text-artis-slate">Total Revenue Generated</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="thms-stat-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-artis-serif text-artis-brown-dark">
              <span className="rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 p-2 shadow-sm">
                <FlaskConical className="h-4 w-4 text-white" />
              </span>
              Lab Operations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-artis-slate">Lab Test Completion Rate</span>
              <span className="text-lg font-semibold text-orange-700">
                {kpis?.totalLabTests
                  ? Math.round(((kpis.totalLabTests - kpis.pendingLabTests) / kpis.totalLabTests) * 100)
                  : 0
                }%
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-700">
                  {kpis?.totalLabTests || 0}
                </div>
                <div className="text-xs text-artis-slate">Total Tests</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-700">
                  {kpis?.pendingLabTests || 0}
                </div>
                <div className="text-xs text-artis-slate">Pending</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 